/**
 * Unsaved-changes navigation guard for dashboard shells.
 *
 * `useShellNavigationBlocker(when)` blocks IN-APP navigation (sidebar links,
 * `navigate()`, back/forward) while `when` is true, and — by default — also
 * arms the browser's native `beforeunload` prompt for tab close / reload /
 * cross-origin navigation.
 *
 * In-app blocking needs a data router: create the shell with
 * `createShell({ router: "data", ... })`. In a classic BrowserRouter shell the
 * hook degrades gracefully to `beforeunload`-only and reports
 * `supported: false` (plus a one-time dev console warning) instead of
 * throwing — React Router's own `useBlocker` hard-crashes outside a data
 * router.
 *
 * @package @plerity/dashboard-shell
 */

import { ConfirmDialog } from "@plerity/ui-core/ConfirmDialog";
import { type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { type BlockerFunction, UNSAFE_DataRouterContext, UNSAFE_DataRouterStateContext } from "react-router-dom";

export type ShellBlockerState = "unblocked" | "blocked" | "proceeding";

export interface ShellNavigationBlocker {
  /** `"blocked"` while a navigation is held waiting for the user's decision. */
  state: ShellBlockerState;
  /** Let the held navigation continue (discard changes). No-op unless blocked. */
  proceed: () => void;
  /** Cancel the held navigation (stay on the page). No-op unless blocked. */
  reset: () => void;
  /** False when the shell is not in `router: "data"` mode (beforeunload-only). */
  supported: boolean;
}

export interface ShellNavigationBlockerOptions {
  /** Also arm the native `beforeunload` prompt while `when` is true (default true). */
  beforeUnload?: boolean;
  /**
   * Fine-grained filter. Default: block only when the PATHNAME changes, so
   * query-string / hash updates on the same page (tabs, zoom, page index)
   * never trigger the prompt.
   */
  shouldBlock?: BlockerFunction;
}

const defaultShouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) =>
  currentLocation.pathname !== nextLocation.pathname;

const UNSUPPORTED: ShellNavigationBlocker = {
  state: "unblocked",
  proceed: () => {},
  reset: () => {},
  supported: false,
};

let warnedUnsupported = false;

/** Arms `beforeunload` while `active`. */
function useBeforeUnload(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require returnValue to be set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);
}

let blockerSeq = 0;

function stripBase(pathname: string, basename: string): string {
  if (basename === "/" || !pathname.toLowerCase().startsWith(basename.toLowerCase())) return pathname;
  const rest = pathname.slice(basename.endsWith("/") ? basename.length - 1 : basename.length);
  return rest.startsWith("/") ? rest : `/${rest}`;
}

/**
 * Guard navigation away from a page with unsaved edits.
 *
 * Mirrors React Router 6.30's `useBlocker` (register a keyed blocker on the
 * data router, read its state from router state) but reads the router
 * contexts NULLABLY, so every hook runs unconditionally and a BrowserRouter
 * shell gets a graceful `supported: false` instead of an invariant crash.
 *
 * @example
 * const blocker = useShellNavigationBlocker(isDirty);
 * return <>{editor}<ShellUnsavedChangesPrompt blocker={blocker} /></>;
 */
export function useShellNavigationBlocker(
  when: boolean,
  options: ShellNavigationBlockerOptions = {}
): ShellNavigationBlocker {
  const { beforeUnload = true, shouldBlock = defaultShouldBlock } = options;
  useBeforeUnload(beforeUnload && when);

  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const routerState = useContext(UNSAFE_DataRouterStateContext);
  const router = dataRouter?.router ?? null;
  const basename = dataRouter?.basename ?? "/";
  const [blockerKey, setBlockerKey] = useState("");

  const blockerFn = useCallback<BlockerFunction>(
    (args) => {
      if (!when) return false;
      if (basename === "/") return shouldBlock(args);
      return shouldBlock({
        ...args,
        currentLocation: { ...args.currentLocation, pathname: stripBase(args.currentLocation.pathname, basename) },
        nextLocation: { ...args.nextLocation, pathname: stripBase(args.nextLocation.pathname, basename) },
      });
    },
    [when, shouldBlock, basename]
  );

  // Key assignment + deletion (tied to the router instance).
  useEffect(() => {
    if (!router) return;
    blockerSeq += 1;
    const key = `shell-blocker-${blockerSeq}`;
    setBlockerKey(key);
    return () => router.deleteBlocker(key);
  }, [router]);

  // (Re)register the blocker function whenever it changes.
  useEffect(() => {
    if (router && blockerKey !== "") router.getBlocker(blockerKey, blockerFn);
  }, [router, blockerKey, blockerFn]);

  const blocker = blockerKey && routerState?.blockers.get(blockerKey);

  // Auto-release a held navigation if the guard turns off (e.g. save finished).
  useEffect(() => {
    if (blocker && blocker.state === "blocked" && !when) blocker.reset();
  }, [blocker, when]);

  if (!router) {
    if (import.meta.env?.DEV && !warnedUnsupported) {
      warnedUnsupported = true;
      console.warn(
        '[dashboard-shell] useShellNavigationBlocker: in-app navigation blocking needs createShell({ router: "data" }); falling back to beforeunload only.'
      );
    }
    return UNSUPPORTED;
  }
  if (!blocker) return { ...UNSUPPORTED, supported: true };
  return {
    state: blocker.state,
    proceed: () => blocker.proceed?.(),
    reset: () => blocker.reset?.(),
    supported: true,
  };
}

export interface ShellUnsavedChangesPromptProps {
  /** Blocker from `useShellNavigationBlocker`. */
  blocker: ShellNavigationBlocker;
  title?: string;
  message?: ReactNode;
  /** Label for "leave and discard" (default "Leave without saving"). */
  confirmLabel?: string;
  /** Label for "stay" (default "Stay on page"). */
  cancelLabel?: string;
}

/**
 * Ready-made confirm dialog (ui-core `ConfirmDialog`, danger variant) for a
 * held navigation. Renders nothing unless `blocker.state === "blocked"`.
 */
export function ShellUnsavedChangesPrompt({
  blocker,
  title = "Leave without saving?",
  message = "You have unsaved changes. If you leave this page they will be lost.",
  confirmLabel = "Leave without saving",
  cancelLabel = "Stay on page",
}: ShellUnsavedChangesPromptProps) {
  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      variant="danger"
      onConfirm={blocker.proceed}
      onCancel={blocker.reset}
    />
  );
}
